import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BigFiveQuestion, BigFiveDimension, bigFiveDimensions, getQuestionsByDimension } from '../data/bigFiveQuestions';
import { BigFiveAnswer, calculateDimensionScore, DimensionScore } from '../services/bigFiveAnalysis';

interface DimensionTestProps {
  dimension: BigFiveDimension['key'];
  onComplete?: (result: DimensionScore) => void;
  onBack?: () => void;
}

export default function DimensionTest({ dimension, onComplete, onBack }: DimensionTestProps) {
  const questions = getQuestionsByDimension(dimension);
  const dimensionInfo = bigFiveDimensions.find(d => d.key === dimension)!;
  
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<BigFiveAnswer[]>([]);
  const [result, setResult] = useState<DimensionScore | null>(null);
  const [isCompleted, setIsCompleted] = useState(false);

  // 如果该维度没有题目，显示提示
  if (questions.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F8F9FA] to-[#E9ECEF] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="text-6xl mb-4">🚧</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">{dimensionInfo.name}测评</h2>
          <p className="text-gray-600 mb-6">该维度的题目正在开发中，敬请期待！</p>
          <button
            onClick={onBack}
            className="bg-[#3A7D78] text-white px-6 py-3 rounded-xl hover:bg-[#2F6B67] transition-colors"
          >
            返回选择
          </button>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const totalQuestions = questions.length;
  const progress = ((currentQuestionIndex + 1) / totalQuestions) * 100;

  // 获取当前题目的答案
  const getCurrentAnswer = () => {
    return answers.find(a => a.questionId === currentQuestion.id)?.selectedOption;
  };

  // 处理答案选择
  const handleAnswerSelect = (score: number) => {
    const newAnswers = answers.filter(a => a.questionId !== currentQuestion.id);
    const newAnswer = {
      questionId: currentQuestion.id,
      selectedOption: score
    };
    newAnswers.push(newAnswer);
    setAnswers(newAnswers);
  };

  // 下一题
  const handleNext = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      completeTest();
    }
  };

  // 上一题
  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  // 完成测试
  const completeTest = () => {
    const dimensionResult = calculateDimensionScore(questions, answers, dimension);
    setResult(dimensionResult);
    setIsCompleted(true);
    onComplete?.(dimensionResult);
  };

  // 重新开始
  const handleRestart = () => {
    setCurrentQuestionIndex(0);
    setAnswers([]);
    setResult(null);
    setIsCompleted(false);
  };

  // 检查是否可以进入下一题
  const canProceed = getCurrentAnswer() !== undefined;

  // 显示结果页面
  if (isCompleted && result) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F8F9FA] to-[#E9ECEF] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl w-full"
        >
          <div className="text-center mb-8">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-3xl font-bold text-gray-800 mb-2">{dimensionInfo.name}测评完成</h2>
            <p className="text-gray-600">{dimensionInfo.description}</p>
          </div>

          <div className="bg-gradient-to-r from-[#3A7D78]/10 to-[#F5B6C1]/10 rounded-xl p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-800">你的得分</h3>
              <div className="text-3xl font-bold text-[#3A7D78]">{result.percentage}%</div>
            </div>
            
            <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${result.percentage}%` }}
                className="bg-gradient-to-r from-[#3A7D78] to-[#F5B6C1] h-3 rounded-full"
                transition={{ duration: 1, delay: 0.5 }}
              />
            </div>
            
            <div className="text-center mb-4">
              <span className="inline-block bg-[#3A7D78] text-white px-4 py-2 rounded-full text-sm font-medium">
                {result.level === 'very_high' && '非常高'}
                {result.level === 'high' && '较高'}
                {result.level === 'moderate' && '中等'}
                {result.level === 'low' && '较低'}
                {result.level === 'very_low' && '很低'}
              </span>
            </div>
            
            <p className="text-gray-700 leading-relaxed">{result.description}</p>
          </div>

          <div className="flex gap-4">
            <button
              onClick={handleRestart}
              className="flex-1 bg-gray-100 text-gray-700 py-3 px-6 rounded-xl hover:bg-gray-200 transition-colors"
            >
              重新测试
            </button>
            <button
              onClick={onBack}
              className="flex-1 bg-[#3A7D78] text-white py-3 px-6 rounded-xl hover:bg-[#2F6B67] transition-colors"
            >
              返回选择
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  // 测试进行中
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F9FA] to-[#E9ECEF] flex items-center justify-center p-4">
      <div className="w-full max-w-4xl">
        {/* 头部信息 */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">{dimensionInfo.name}测评</h1>
          <p className="text-gray-600 mb-4">{dimensionInfo.description}</p>
          <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
            <span>
              题目 {currentQuestionIndex + 1} / {totalQuestions}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 mt-4">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              className="bg-gradient-to-r from-[#3A7D78] to-[#F5B6C1] h-2 rounded-full"
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>

        {/* 题目卡片 */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestionIndex}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-2xl shadow-xl p-8 mb-8"
          >
            <div className="mb-6">
              <div className="text-sm text-[#3A7D78] font-medium mb-2 bg-[#3A7D78]/10 px-3 py-1 rounded-full inline-block">
                {dimensionInfo.name.toUpperCase()}
              </div>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                场景 {currentQuestion.id}
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                {currentQuestion.question}
              </p>
            </div>

            {/* 选项描述 */}
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="p-6 bg-[#3A7D78]/10 rounded-xl border border-[#3A7D78]/20 text-center">
                <div className="text-3xl font-bold text-[#3A7D78] mb-4">A</div>
                <p className="text-gray-700 text-sm leading-relaxed">
                  {currentQuestion.options.a}
                </p>
              </div>
              <div className="p-6 bg-[#F5B6C1]/10 rounded-xl border border-[#F5B6C1]/20 text-center">
                <div className="text-3xl font-bold text-[#F5B6C1] mb-4">B</div>
                <p className="text-gray-700 text-sm leading-relaxed">
                  {currentQuestion.options.b}
                </p>
              </div>
            </div>

            {/* 评分选择 */}
            <div className="mb-6">
              <div className="text-center mb-4">
                <p className="text-gray-600 text-sm mb-2">
                  请选择您更倾向于哪个选项，以及倾向程度：
                </p>
                <div className="flex justify-between text-xs text-gray-500 mb-2">
                  <span>完全倾向A</span>
                  <span>中性</span>
                  <span>完全倾向B</span>
                </div>
              </div>
              
              <div className="flex justify-center space-x-2">
                {[0, 1, 2, 3, 4].map((score) => {
                  const currentAnswer = getCurrentAnswer();
                  const isSelected = currentAnswer === score;
                  
                  return (
                    <motion.button
                      key={score}
                      onClick={() => handleAnswerSelect(score)}
                      className={`w-12 h-12 rounded-full border-2 transition-all duration-200 flex items-center justify-center ${
                        isSelected
                          ? 'border-[#3A7D78] bg-[#3A7D78] text-white shadow-lg'
                          : 'border-gray-300 hover:border-[#3A7D78] hover:bg-[#3A7D78]/10 text-gray-700'
                      }`}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <span className="font-medium">{score}</span>
                    </motion.button>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* 导航按钮 */}
        <div className="flex justify-between items-center">
          <button
            onClick={currentQuestionIndex === 0 ? onBack : handlePrevious}
            className="bg-gray-100 text-gray-700 py-3 px-6 rounded-xl hover:bg-gray-200 transition-colors"
          >
            {currentQuestionIndex === 0 ? '返回选择' : '上一题'}
          </button>
          
          <button
            onClick={handleNext}
            disabled={!canProceed}
            className={`py-3 px-6 rounded-xl transition-colors ${
              canProceed
                ? 'bg-[#3A7D78] text-white hover:bg-[#2F6B67]'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {currentQuestionIndex === totalQuestions - 1 ? '完成测评' : '下一题'}
          </button>
        </div>
      </div>
    </div>
  );
}